import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchJobEvents } from '../../store/jobEventsSlice';
import { prettifyTimestamp } from '../../tables/common';
import { REJECTED_NAMES } from '../../tables/RejectedTable';
import { useModals } from '@mattjennings/react-modal-stack';
import InvoiceSentModal from '../../components/InvoiceSentModal';
import Chevron from '../../components/Chevron';

// Labels for machine-readable event types written by the backend (job_events.type)
const EVENT_LABELS = {
    query_received:         'Päring saabus',
    offer_sent:             'Hinnapakkumine saadetud',
    price_changed:          'Hinda muudetud',
    client_accepted:        'Klient kinnitas pakkumise',
    client_rejected:        'Klient loobus pakkumisest',
    job_started:            'Juht ja buss määratud',
    drivers_changed:        'Juhte muudetud',
    buses_changed:          'Busse muudetud',
    driver_confirmed:       'Juht kinnitas töö',
    driver_rejected:        'Juht keeldus tööst',
    driver_hours_submitted: 'Juhi tunnid laekusid',
    driver_feedback_edited: 'Juhi tagasisidet muudetud',
    route_changed:          'Marsruuti muudetud',
    query_field_changed:    'Andmeid muudetud',
    food_packages_changed:  'Toitlustuspakette muudetud',
    emails_sent:            'E-kirjad saadetud',
    query_deleted:          'Töö kustutatud',
    invoice_sent:           'Arve saadetud',
    prepayment_invoice_sent: 'Ettemaksu arve saadetud',
    archived:               'Arhiivi liigutatud',
};

const FIELD_LABELS = {
    start:           'Start',
    passengers:      'Reisijaid',
    fullname:        'Tellija täisnimi',
    email:           'Tellija e-mail',
    phone:           'Tellija telefon',
    company:         'Firma',
    notes:           'Kliendi lisainfo',
    logistics_notes: '"Pane tähele" tekst',
    driver_notes:    'Märkmed juhile',
};

function parseDetails(event) {
    try { return event["job_event_details"] ? JSON.parse(event["job_event_details"]) : {}; }
    catch (e) { return {}; }
}

function eventTitle(event) {
    const details = parseDetails(event);
    const type = event["job_event_type"];
    if(type === 'query_field_changed') {
        return `${FIELD_LABELS[details.field] || details.field || 'Väli'} muudetud`;
    }
    if(type === 'client_rejected' && details.reason) {
        return `${EVENT_LABELS[type]} (${REJECTED_NAMES[details.reason] || details.reason})`;
    }
    if(type === 'offer_sent' && details.again) {
        return 'Hinnapakkumine saadetud uuesti';
    }
    if((type === 'driver_hours_submitted' || type === 'driver_feedback_edited') && details.driver) {
        return `${EVENT_LABELS[type]} (${details.driver})`;
    }
    return EVENT_LABELS[type] || type;
}

function actorLabel(event) {
    const name = event["job_event_actor_name"];
    switch(event["job_event_actor_type"]) {
        case 'client': return name ? `Klient (${name})` : 'Klient';
        case 'driver': return name ? `Juht (${name})` : 'Juht';
        case 'system': return 'Süsteem';
        default:       return name || event["job_event_actor_username"] || 'Kasutaja';
    }
}

// Milestones that only appear once the matching event has been logged
// ("Arve" / "Ettemaksu arve" buttons). They are not fixed steps: each one is
// slotted into the overview right after the step the job had reached at the
// time it was marked, so a prepayment invoice sent before the trip shows
// before "Reisi kuupäev" and a final invoice after "Juhi tagasiside".
const DYNAMIC_MILESTONES = {
    prepayment_invoice_sent: 'Ettemaksu arve saadetud',
    invoice_sent:            'Arve saadetud',
};

function insertDynamicMilestones(milestones, events) {
    const result = milestones.slice();
    events
        .filter(e => DYNAMIC_MILESTONES[e["job_event_type"]])
        .sort((a, b) => a["job_event_created"] - b["job_event_created"])
        .forEach(e => {
            const at = e["job_event_created"];
            let index = 0;
            result.forEach((m, i) => { if(m.at && m.at <= at) index = i + 1; });
            result.splice(index, 0, { label: DYNAMIC_MILESTONES[e["job_event_type"]], at });
        });
    return result;
}

/** Milestones shown in the progress overview (design: "Töö elukaar"). */
function buildMilestones(data, events) {
    const lastOf = (type, pred) => {
        const matching = events.filter(e => e["job_event_type"] === type && (!pred || pred(e)));
        return matching.length ? matching[matching.length - 1]["job_event_created"] : null;
    };
    const now = Math.floor(Date.now() / 1000);
    const accepted = data["query_response_status"] === 'accepted';

    return insertDynamicMilestones([
        { label: 'Päring',                     at: lastOf('query_received') || data["query_created"] || null },
        { label: 'Hinnapakkumine',             at: lastOf('offer_sent') || data["query_response_created"] || null },
        { label: 'Kliendi poolt kinnitatud',   at: lastOf('client_accepted') || (accepted ? data["query_response_updated"] : null) },
        { label: 'Juht ja buss määratud',      at: lastOf('job_started') || data["job_created"] || null },
        { label: 'Reisi kuupäev',              at: (data["query_start"] && data["query_start"] <= now) ? data["query_start"] : null, planned: data["query_start"] || null },
        { label: 'Juhi tagasiside',            at: lastOf('driver_hours_submitted') },
        { label: 'Arhiivi liigutamine',        at: lastOf('archived') },
    ], events);
}

const TRUNCATE_CHARS = 160;
const TRUNCATE_LINES = 3;

function isLong(text) {
    if(text == null) return false;
    const str = String(text);
    return str.length > TRUNCATE_CHARS || str.split('\n').length > TRUNCATE_LINES;
}

function truncate(text) {
    const str = String(text);
    const lines = str.split('\n').slice(0, TRUNCATE_LINES).join('\n');
    const cut = lines.length > TRUNCATE_CHARS ? lines.slice(0, TRUNCATE_CHARS) : lines;
    return cut.trimEnd() + '…';
}

/** Old → new value line; long texts are cut with a "Näita kogu sisu" toggle. */
function EventValues({ oldValue, newValue }) {
    const [ expanded, setExpanded ] = useState(false);
    const hasOld = oldValue != null && oldValue !== '';
    const newText = (newValue == null || newValue === '') ? '(tühi)' : newValue;
    const long = isLong(oldValue) || isLong(newText);
    const show = (text) => (long && !expanded && isLong(text)) ? truncate(text) : text;

    return (
        <span style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {hasOld && <><s style={{ color: '#a33' }}>{show(oldValue)}</s>{' → '}</>}
            <span style={{ color: '#1f6f3f' }}>{show(newText)}</span>
            {long && (
                <>
                    {' '}
                    <button className="borderless" style={{ padding: 0, fontSize: 13 }} onClick={() => setExpanded(!expanded)}>
                        {expanded ? 'Peida' : 'Näita kogu sisu'}
                    </button>
                </>
            )}
        </span>
    );
}

const styles = {
    wrap:      { width: 340, maxWidth: '100%', marginTop: 24, paddingTop: 16, borderTop: '1px solid #ddd' },
    header:    { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, cursor: 'pointer' },
    milestone: { display: 'grid', gridTemplateColumns: '1fr 14px', columnGap: 12, alignItems: 'start' },
    bar:       { width: 14, borderRadius: 3, background: '#e6e6e6', overflow: 'hidden' },
    barFill:   { width: '100%', background: '#2e8b6f' },
    event:     { display: 'grid', gridTemplateColumns: '1fr', rowGap: 2, paddingLeft: 10, borderLeft: '2px solid #ddd' },
};

/**
 * "Töö elukaar": milestone overview + full chronological event log.
 * Admin-only (the endpoint enforces it too); rendered at the bottom of the
 * right-hand details column.
 */
function JobLifecycle({ id }) {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.knownData[id]);
    const events = useSelector((state) => state.jobEvents[id]) || [];
    const [ showAll, setShowAll ] = useState(false);
    // Whole section is collapsed by default: it takes a lot of vertical space
    const [ open, setOpen ] = useState(false);
    const { openModal } = useModals();

    const invoiceSent = events.some(e => e["job_event_type"] === 'invoice_sent');
    const prepaymentInvoiceSent = events.some(e => e["job_event_type"] === 'prepayment_invoice_sent');

    // "Arve" / "Ettemaksu arve": invoicing happens outside the platform, so the
    // logistician marks it here by hand after confirming in a dialog. Writes a
    // lifecycle event only; does not archive. Each button disappears once its
    // event exists.
    function onInvoiceSent(kind) {
        openModal(InvoiceSentModal, {
            id,
            kind,
            jobNumber: data["job_sequential_id"],
            onConfirmed: () => dispatch(fetchJobEvents({ id })),
        });
    }

    // Refetch whenever the query data is refetched
    useEffect(() => {
        dispatch(fetchJobEvents({ id }));
    }, [dispatch, id, data]);

    const milestones = useMemo(() => (data ? buildMilestones(data, events) : []), [data, events]);
    if(!data) return null;

    // Progress = up to the furthest milestone reached. Steps can be skipped
    // (e.g. archived without marking the invoice sent); a skipped step stays
    // dimmed but the bar still reaches the later one.
    let reached = 0;
    milestones.forEach((m, i) => { if(m.at) reached = i + 1; });
    const progress = milestones.length ? (reached / milestones.length) : 0;

    return (
        <div className="column g16" style={styles.wrap}>
            <div className="noselect" style={styles.header} onClick={() => setOpen(!open)}>
                <h2>Töö elukaar</h2>
                <Chevron open={open} />
            </div>

            {open && <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 14px', columnGap: 12 }}>
                <div className="column g8">
                    {milestones.map((m, i) => (
                        <div key={i} className="column" style={{ textAlign: 'right', opacity: m.at ? 1 : 0.45 }}>
                            <b>{m.label}</b>
                            <span style={{ fontSize: 13, color: '#666' }}>
                                {m.at ? prettifyTimestamp(m.at) : (m.planned ? `plaanis ${prettifyTimestamp(m.planned)}` : '–')}
                            </span>
                        </div>
                    ))}
                </div>
                <div style={styles.bar}>
                    <div style={{ ...styles.barFill, height: `${Math.round(progress * 100)}%` }} />
                </div>
            </div>

            {(data["job_query_id"] && (!prepaymentInvoiceSent || !invoiceSent)) && (
                <div className="row g8" style={{ justifyContent: 'flex-end' }}>
                    {!prepaymentInvoiceSent && <button onClick={() => onInvoiceSent('prepayment')}>Ettemaksu arve</button>}
                    {!invoiceSent && <button onClick={() => onInvoiceSent('invoice')}>Arve</button>}
                </div>
            )}

            <button className="borderless" onClick={() => setShowAll(!showAll)} style={{ alignSelf: 'flex-end' }}>
                {showAll ? 'Peida sündmused' : `Kõik sündmused (${events.length})`}
            </button>

            {showAll && (
                <div className="column g12" style={{ gap: 12 }}>
                    {events.length === 0 && <p style={{ color: '#888' }}>Sündmusi pole veel salvestatud.</p>}
                    {events.map(event => {
                        const hasValues = (event["job_event_old_value"] != null && event["job_event_old_value"] !== '') || (event["job_event_new_value"] != null && event["job_event_new_value"] !== '');
                        return (
                            <div key={event["job_event_id"]} style={styles.event}>
                                <span style={{ fontSize: 13, color: '#666' }}>
                                    {prettifyTimestamp(event["job_event_created"])} · {actorLabel(event)}
                                </span>
                                <b>{eventTitle(event)}</b>
                                {hasValues && (
                                    <EventValues oldValue={event["job_event_old_value"]} newValue={event["job_event_new_value"]} />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            </>}
        </div>
    );
}

export default JobLifecycle;
